import type { ThemePresetId } from '../../styles/themePresets';

export type GridDensity ='comfortable' | 'compact' | 'dense';
export type FontSize = 'small' | 'medium' | 'large';
export type FontWeight = 'light' | 'normal' | 'bold';
export type ThemeMode = 'light' | 'dark';
/** Display size from the avatar menu, as a CSS zoom factor on the whole app. */
export type DisplayZoom = 0.8 | 0.9 | 1 | 1.1;
export const DISPLAY_ZOOM_OPTIONS: DisplayZoom[] = [0.8, 0.9, 1, 1.1];

export interface UserPreferences {
    gridDensity: GridDensity;
    fontSize: FontSize;
    fontWeight: FontWeight;
    columnStates: Record<string, any[]>; // pageKey -> AG Grid ColumnState[]
    /**
     * pageKey -> colId -> width, written ONLY when the user drags a column
     * edge. Deliberately separate from `columnStates`: that one is saved on
     * every sort / move / visibility change, so the widths inside it are
     * whatever the last automatic sizing pass produced, not a choice anyone
     * made. Grids auto-size to content unless a pageKey appears here, so the
     * two could not share a home — honouring `columnStates`' widths would mean
     * one stray sort click froze a grid's widths forever.
     */
    columnWidths: Record<string, Record<string, number>>;
    themeMode?: ThemeMode; // account-level light/dark preference (follows the user across devices)
    themePreset?: ThemePresetId; // colour theme from the avatar menu, light mode only; absent = company default
    displayZoom?: DisplayZoom; // account-level display size; absent = 100%
    sidebarWidth?: number; // dragged sidebar width in px; absent = the company default (Appearance)
    /**
     * The dashboard's personal settings — quick actions, widgets, card order and
     * size, rate-chart choices — by their storage key (see routes/Home/dashboardPrefs).
     * Absent until the user's browser copy has been uploaded once.
     */
    dashboard?: Record<string, unknown>;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
    gridDensity: 'compact',
    fontSize: 'medium',
    fontWeight: 'normal',
    columnStates: {},
    columnWidths: {}
};

export const FONT_WEIGHT_MAP: Record<FontWeight, number> = {
    light: 300,
    normal: 400,
    bold: 700
};

export interface GridDensityConfig {
    rowHeight: number;
    headerHeight: number;
    fontSize: number;
    lineHeight: number;
}

export const GRID_DENSITY_MAP: Record<GridDensity, GridDensityConfig> = {
    comfortable: { rowHeight: 40, headerHeight: 40, fontSize: 14, lineHeight: 1.4 },
    compact: { rowHeight: 32, headerHeight: 34, fontSize: 13, lineHeight: 1.3 },
    dense: { rowHeight: 28, headerHeight: 30, fontSize: 12, lineHeight: 1.2 }
};

export const FONT_SIZE_MAP: Record<FontSize, number> = {
    small: 11,
    medium: 13,
    large: 15
};